/**
 * Shared completion plumbing for the agents-onboarding research run.
 *
 * The research itself runs server-side (asyncio.shield on POST
 * /agents/onboarding); the FE learns it finished by polling GET
 * /agents/profile and comparing profile_data against the baseline captured
 * in the localStorage in-flight mark (agent-utils.ts). Historically only
 * OnboardingSetup did that watching, so completion was invisible unless the
 * card was mounted — and when TWO cards were mounted (/agents and the
 * /clu first-run both render OnboardingSetup), each fired its own
 * "profile ready" toast.
 *
 * This module centralizes both concerns:
 *   - useOnboardingResearchWatcher(): app-wide watcher (mounted once in
 *     AppEffects, and by any surface that wants the live in-flight flag).
 *     Polls while the in-flight mark exists and fires the success toast on
 *     completion, wherever the user is.
 *   - useFireProfileReadyToast(): the toast itself. Its "Confirm profile"
 *     CTA only NAVIGATES to the review card (/agents) — it never approves
 *     or searches on the user's behalf. Deduped across ALL callers via a
 *     module-scoped guard keyed on the profile content, so N concurrent
 *     watchers still produce exactly one toast.
 */

import { useGetProfileQuery } from '@/store/features/agents.feature';
import { useOrganization } from '@clerk/react';
import type { OrgProfileData } from '@/types';
import { useCallback, useEffect, useState } from 'react';
import {
  clearInFlight,
  profileHasDraft,
  readInFlight,
  stableStringify,
} from './agent-utils';

// Module-scoped (NOT per-instance) one-shot guard. Keyed on the profile
// JSON rather than a boolean so a re-run producing a new profile announces
// again, while the same completion observed by multiple mounted watchers
// (AppEffects + /clu + an /agents card) announces exactly once.
let toastedProfileJson: string | null = null;

/**
 * The finished profile, waiting to be shown. Setup sends the user straight to
 * Home when a run starts, so completion has to come back to them wherever
 * they've gone — this is the handoff between the app-wide watcher and the
 * dialog that surfaces it. A module store (rather than component state) keeps
 * it alive across the navigation that happens in between.
 */
let pendingProfile: OrgProfileData | null = null;
const pendingListeners = new Set<() => void>();

const emitPending = () => pendingListeners.forEach((listener) => listener());

export function subscribeToProfileReady(listener: () => void): () => void {
  pendingListeners.add(listener);
  return () => pendingListeners.delete(listener);
}

export function getPendingProfile(): OrgProfileData | null {
  return pendingProfile;
}

export function clearPendingProfile(): void {
  if (pendingProfile === null) return;
  pendingProfile = null;
  emitPending();
}

/** Re-arm the completion notice for a fresh research run (call on submit). */
export function resetProfileReadyToast(): void {
  toastedProfileJson = null;
  clearPendingProfile();
}

/**
 * One-shot "profile ready" announcement. Publishes the finished profile so
 * AgentProfileReadyDialog can open it in front of whatever the user is doing.
 *
 * A toast used to carry this, but the run starts on /agents and immediately
 * sends the user to Home, so the result lands minutes later somewhere else
 * entirely — a toast that auto-dismisses is too easy to miss for the one
 * artifact the user asked us to build. Nothing here approves or searches on
 * the user's behalf; the dialog only shows the result and offers to open it.
 */
export function useFireProfileReadyToast() {
  return useCallback((profile: OrgProfileData) => {
    const profileJson = stableStringify(profile);
    if (toastedProfileJson === profileJson) return;
    toastedProfileJson = profileJson;
    pendingProfile = profile;
    emitPending();
  }, []);
}

/**
 * App-wide research watcher. Returns true while a research run is
 * in-flight (localStorage mark present). While in-flight, polls
 * GET /agents/profile every 4s; on completion clears the mark and fires
 * the success toast. Multiple concurrent mounts are safe: RTK Query
 * dedupes the poll into one subscription, and the toast is module-guarded.
 */
export function useOnboardingResearchWatcher(): boolean {
  // Only this org's run: a mark left by another org (before a switch) would
  // compare its baseline against this org's profile and fire a false toast.
  const organizationId = useOrganization().organization?.id;
  const [mark, setMark] = useState(() => readInFlight(organizationId));
  const fireProfileReadyToast = useFireProfileReadyToast();

  // While idle, cheaply re-check localStorage so a run started by another
  // component (or another tab) is picked up without prop threading.
  useEffect(() => {
    if (mark) return;
    const id = setInterval(() => {
      const next = readInFlight(organizationId);
      if (next) setMark(next);
    }, 4000);
    return () => clearInterval(id);
  }, [mark, organizationId]);

  const { data: profile } = useGetProfileQuery(undefined, {
    skip: !mark,
    pollingInterval: 4000,
  });

  useEffect(() => {
    if (!mark) return;
    // The mark can vanish underneath us — 10-min TTL expiry, or an
    // OnboardingSetup card completing first and clearing it. Re-read on
    // every poll tick so the in-flight flag self-heals.
    if (!readInFlight(organizationId)) {
      setMark(null);
      return;
    }
    if (!profile) return;
    const currentJson = stableStringify(profile.profile_data ?? {});
    // A setup run saves its answers before the research lands, so only a
    // draft (a name or an ICP) is a ready profile.
    if (
      currentJson !== mark.baseline_profile_json &&
      profileHasDraft(profile.profile_data)
    ) {
      clearInFlight(organizationId);
      setMark(null);
      fireProfileReadyToast(profile.profile_data);
    }
  }, [mark, profile, fireProfileReadyToast, organizationId]);

  return mark != null;
}
